import React from 'react';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useUpdateTunnelProfileSettings } from 'providers/api';
import { i18n } from 'i18n';
import { Model } from '../../../hooks/useModelState';
import ToolbarSection from './ToolbarSection';
import ToolbarButton from './ToolbarButton';
import ToolbarTooltip from './ToolbarTooltip';

interface TunnelProfileDisplayControlsProps {
  model: Model;
}

const opacityToTransparency = (opacity: number) => Math.round((1 - opacity) * 100);
const transparencyToOpacity = (transparency: number) => 1 - (transparency / 100);

const TunnelProfileDisplayControls = ({ model }: TunnelProfileDisplayControlsProps) => {
  const projectId = model.project.entityId as string;
  const updateSettingsMutation = useUpdateTunnelProfileSettings(projectId);
  const { tunnelProfile } = model;
  const transparency = tunnelProfile ? opacityToTransparency(tunnelProfile.opacity) : opacityToTransparency(0.35);

  const handleToggleVisibility = () => {
    if (!tunnelProfile) return;
    const visible = !tunnelProfile.visible;
    model.setTunnelProfileVisible(visible);
    updateSettingsMutation.mutate({ visible, opacity: tunnelProfile.opacity });
  };

  const handleTransparencyChange = (_: Event, value: number | number[]) => {
    if (!tunnelProfile || Array.isArray(value)) return;
    model.setTunnelProfileOpacity(transparencyToOpacity(value));
  };

  const handleTransparencyCommitted = (_: React.SyntheticEvent | Event, value: number | number[]) => {
    if (!tunnelProfile || Array.isArray(value)) return;
    updateSettingsMutation.mutate({ visible: tunnelProfile.visible, opacity: transparencyToOpacity(value) });
  };

  return (
    <ToolbarSection label={i18n._('Tunnel')}>
      <ToolbarButton
        label={i18n._('Visible')}
        tooltipTitle={i18n._('Toggle Tunnel Profile')}
        description={tunnelProfile ? tunnelProfile.fileName : undefined}
        disabledDescription={i18n._('Import a tunnel profile first (Editor mode, Import/Export tab).')}
        icon={<VisibilityIcon fontSize="small" />}
        onClick={handleToggleVisibility}
        disabled={!tunnelProfile}
        selected={!!tunnelProfile?.visible}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 104, px: 0.5 }}>
        <ToolbarTooltip
          title={i18n._('Profile Transparency')}
          description={tunnelProfile ? tunnelProfile.fileName : i18n._('Import a tunnel profile first (Editor mode, Import/Export tab).')}
        >
          <Box sx={{ width: 96, height: 28, display: 'flex', alignItems: 'center' }}>
            <Slider
              size="small"
              min={0}
              max={95}
              value={transparency}
              onChange={handleTransparencyChange}
              onChangeCommitted={handleTransparencyCommitted}
              disabled={!tunnelProfile}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${value}%`}
              color="secondary"
              sx={{ py: 0 }}
            />
          </Box>
        </ToolbarTooltip>
        <Typography
          variant="caption"
          sx={{
            fontSize: 10,
            lineHeight: 1.1,
            textAlign: 'center',
            color: tunnelProfile ? 'text.primary' : 'text.disabled',
          }}
        >
          {i18n._('Transparency')}
        </Typography>
      </Box>
    </ToolbarSection>
  );
};

export default TunnelProfileDisplayControls;
